// lib
import { useContext, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/components/context'
import { DataContext } from '@aytm/app/stats/page/sidebar/filters/FiltersPanel'

import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'

// components
import Popup, { PopupCloseButton } from '@aytm/lib/rfd-ui/components/basic/popup'

import FiltersRowCustomVariable from '@aytm/app/stats/page/sidebar/filters/rows/FiltersRowCustomVariable'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'

const FiltersGroupCustomVariables = ({ group, opened = true, getFilterValues, getFilterOptions }) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)
  const { features, onVariableUpload } = useContext(DataContext)

  const [messages, setMessages] = useState(sidebarState.variables.messages || [])
  const [errors, setErrors] = useState(sidebarState.variables.errors || [])
  const [warnings, setWarnings] = useState(sidebarState.variables.warnings || [])

  const [uploading, setUploading] = useState(sidebarState.variables.uploading || false)
  const [processing, setProcessing] = useState(sidebarState.variables.processing || false)

  const inputRef = useRef(null)
  const dropTargetRef = useRef(null)

  const closePopup = () =>
    sidebarDispatch({
      type: 'UPDATE_STATE',
      key: 'variables',
      state: {
        messages: [],
        errors: [],
        warnings: []
      }
    })

  const onUpload = file => {
    closePopup()

    onVariableUpload(file)
  }

  const dropTarget = ADND.targets
    .files()
    .onDrop((...files) => {
      if (!files.length) return
      onUpload(files[0])
    })
    .acceptDrop(!uploading && !processing)

  const onChange = () => {
    if (!inputRef.current?.files.length) return
    onUpload(inputRef.current.files[0])
  }

  useEffect(() => {
    if (dropTarget) dropTarget.connect(dropTargetRef.current)

    return () => {
      if (dropTarget) dropTarget.disconnect()
    }
  }, [dropTarget])

  useNonInitialEffect(() => {
    setMessages(sidebarState.variables.messages || [])
    setErrors(sidebarState.variables.errors || [])
    setWarnings(sidebarState.variables.warnings || [])

    setUploading(sidebarState.variables.uploading || false)
    setProcessing(sidebarState.variables.processing || false)

    // TODO
    dropTargetRef.current.scrollIntoView(false)
  }, [sidebarState.variables])

  const renderUploaderActiveState = () => (
    <>
      {!uploading && !processing && (0 < messages.length || 0 < warnings.length || 0 < errors.length) && (
        <Popup
          className={classNames(style['component-popup'], {
            'rfd-ui-popup-note': 0 < warnings.length,
            'rfd-ui-popup-error': 0 < errors.length
          })}
          width="234px"
          direction="topleft"
          show
          content={() => (
            <>
              <PopupCloseButton className={style['component-popup-close']} onClose={closePopup}>
                <i className="icon-aytmfd-x-v2" />
              </PopupCloseButton>
              {0 < messages.length ? (
                <div dangerouslySetInnerHTML={{ __html: messages.join('<br/><br/>') }}></div>
              ) : null}
              {0 < warnings.length || 0 < errors.length ? (
                <div dangerouslySetInnerHTML={{ __html: errors.concat(warnings).join('<br/><br/>') }}></div>
              ) : null}
            </>
          )}
        >
          <div className={`${style['component-popup-anchor']} rfd-ui-popup-target-anchor`} />
        </Popup>
      )}
      <div ref={dropTargetRef} className="p-component p-button p-button-rounded p-button-blue">
        {uploading || processing ? (
          <>
            <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
            {uploading ? 'Uploading...' : 'Processing...'}
          </>
        ) : (
          <>
            <i className="icon-aytmfd-export-v2" />
            <span id={`stat-filters-group-${group.key}-upload-label`}>
              {(0 < group.values.length && 'Upload more variables') || 'Upload CSV/XLSX'}
            </span>
            <input
              ref={inputRef}
              data-testid={`stat-filters-group-${group.key}-upload-input`}
              aria-labelledby={`stat-filters-group-${group.key}-upload-label`}
              type="file"
              name="file"
              accept=".xlsx, .xls, .csv"
              tabIndex={-1}
              multiple={false}
              onChange={onChange}
            />
          </>
        )}
      </div>
    </>
  )

  const renderUploaderDisabledState = () =>
    features.data_lab_upload.available && features.data_lab_upload.onPackage ? (
      <Popup
        className="rfd-ui-popup-warning"
        targetClassName="p-field p-col-12"
        width="254px"
        trigger="hover"
        direction="topleft"
        portal
        content={() => (
          <div>
            <p>
              {/* PME2 (AYTM-11321): render server-authored `text` verbatim; guard the icon, which the
                  already-on-Enterprise Contact-Us variant omits. */}
              {features.data_lab_upload.onPackage.icon ? (
                <span className={`stat-upgrade-icon stat-upgrade-icon-${features.data_lab_upload.onPackage.id}`}>
                  <i className={`icon-aytmfd-${features.data_lab_upload.onPackage.icon}`} />
                </span>
              ) : null}
              <span dangerouslySetInnerHTML={{ __html: features.data_lab_upload.onPackage.text }} />
            </p>
          </div>
        )}
      >
        <a
          className="p-component p-button p-button-rounded p-button-blue"
          href={features.data_lab_upload.onPackage.upgradeURL}
          target="_blank"
          rel="noreferrer"
        >
          <i className="icon-aytmfd-lock" />
          <span
            dangerouslySetInnerHTML={{
              __html: (0 < group.values.length && 'Upload more variables') || 'Upload CSV/XLSX'
            }}
          ></span>
        </a>
      </Popup>
    ) : (
      <div className="p-component p-button p-button-rounded p-button-blue p-disabled">
        <i className="icon-aytmfd-lock" />
        <span
          dangerouslySetInnerHTML={{
            __html: (0 < group.values.length && 'Upload more variables') || 'Upload CSV/XLSX'
          }}
        ></span>
      </div>
    )

  return (
    <>
      {group.values.map(filter => (
        <FiltersRowCustomVariable
          key={filter.subkey ? `${filter.key}${filter.subkey}` : filter.key}
          filter={filter}
          removable={opened}
          filterValues={getFilterValues(filter)}
          filterOptions={getFilterOptions(filter)}
        />
      ))}
      {features.data_lab_upload.hidden ? null : (
        <div ref={dropTargetRef} className={`${style['filter-row']} ${style['filter-upload']} p-fluid`}>
          <div
            data-testid={`stat-filters-group-${group.key}-upload`}
            data-disabled={features.data_lab_upload.disabled}
            className={style['filter-upload-button']}
          >
            {features.data_lab_upload.disabled ? renderUploaderDisabledState() : renderUploaderActiveState()}
          </div>
        </div>
      )}
    </>
  )
}

FiltersGroupCustomVariables.propTypes = {
  group: PropTypes.object,
  opened: PropTypes.bool,
  getFilterValues: PropTypes.func,
  getFilterOptions: PropTypes.func
}

FiltersGroupCustomVariables.displayName = 'FiltersGroupCustomVariables'

export default FiltersGroupCustomVariables
